/**
 * SkillTypeaheadChipList — chip list with freehand + typeahead suggestions.
 *
 * Renders existing skills as removable chips and an input that:
 * - Offers the skills catalog grouped by category. A category is a pick of its
 *   own (the whole category), with its skills indented under it
 * - Accepts freehand entries only where `allowFreehand` says so (#1345)
 * - Nudges "Did you mean X?" when the typed term is close to a catalog one
 *
 * Where free text is off, that nudge is the only thing standing between the
 * user and a dead end, so it is load-bearing: it must render with nothing
 * behind it, and it must be reachable without a mouse.
 */

import { useRef, useState } from 'react'
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { Dismiss12Regular, FolderRegular } from '@fluentui/react-icons'
import { useCapabilities } from '../hooks/useCapabilities'
import { useSkillSuggestions } from '../hooks/useSkillSuggestions'
import {
  WHOLE_CATEGORY_NOTE,
  groupCatalog,
  hasCategories,
  searchCatalog,
  skillHoverText,
} from '../lib/skillCatalog'
import { skillKey } from '../lib/skillMatch'
import type { Capability } from '../types/capability'
import { useToast } from './ToastProvider'
import { bayerColors, surfaces } from '../theme'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    borderRadius: '9999px',
    background: bayerColors.navyLight,
    color: bayerColors.navy,
    fontSize: '12px',
    fontWeight: 600,
  },
  // A grandfathered term: amber, so it reads as "different" before anyone
  // hovers for the why.
  chipLegacy: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    borderRadius: '9999px',
    background: '#fdf3e3',
    color: '#7a5f00',
    border: '1px solid #f0d9a0',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'help',
  },
  chipX: {
    cursor: 'pointer',
    display: 'inline-flex',
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: bayerColors.navy,
    ':hover': { color: '#b10e1c' },
  },
  inputWrapper: {
    position: 'relative',
    display: 'inline-flex',
    flexDirection: 'column',
  },
  input: {
    fontSize: '12px',
    padding: '5px 10px',
    borderRadius: '9999px',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    outline: 'none',
    background: '#FFFFFF',
    minWidth: '180px',
    ':focus': {
      borderTopColor: bayerColors.navyMid,
      borderRightColor: bayerColors.navyMid,
      borderBottomColor: bayerColors.navyMid,
      borderLeftColor: bayerColors.navyMid,
      boxShadow: '0 0 0 3px rgba(43,107,166,0.12)',
    },
  },
  // Wider than the input: skill names run to ~70 characters, and each carries
  // a line of description.
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: 'min(440px, calc(100vw - 48px))',
    marginTop: '4px',
    maxHeight: '320px',
    overflowY: 'auto',
    background: '#FFFFFF',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: '6px',
    boxShadow: tokens.shadow8,
    zIndex: 1000,
    padding: '4px 0',
  },
  option: {
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ':hover': { background: surfaces.proposalCardBg },
  },
  optionActive: {
    background: bayerColors.navyLight,
    ':hover': { background: bayerColors.navyLight },
  },
  // A skill under its category.
  optionIndented: {
    paddingLeft: '30px',
  },
  optionText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1,
  },
  optionCategory: {
    fontWeight: tokens.fontWeightSemibold,
  },
  // One line, cut short; the whole of it, and the examples, are on hover.
  optionDesc: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  optionTag: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
  },
  groupHeading: {
    padding: '6px 12px 2px',
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  freehandOption: {
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    fontStyle: 'italic',
    color: bayerColors.navyMid,
    ':hover': { background: surfaces.proposalCardBg },
  },
  nudge: {
    padding: '6px 12px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    background: '#fffbe6',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: '#f0e68c',
  },
  nudgeSuggestion: {
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    color: bayerColors.navyMid,
    background: 'transparent',
    border: 'none',
    padding: '0 2px',
    cursor: 'pointer',
    textDecorationLine: 'underline',
    ':hover': { color: bayerColors.navy },
  },
  loadError: {
    padding: '6px 12px',
    fontSize: '11px',
    color: tokens.colorPaletteRedForeground1,
  },
  addButton: {
    fontSize: '12px',
    color: bayerColors.navyMid,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '3px 6px',
    borderRadius: '4px',
    ':hover': { background: bayerColors.navyLight },
  },
})

export interface SkillTypeaheadChipListProps {
  values: string[]
  onSave: (values: string[]) => void
  placeholder?: string
  addLabel?: string
  disabled?: boolean
  /**
   * Accessible name for the input. The input only exists once "+ Add" is
   * pressed, so a caller's own `<label htmlFor>` would point at nothing for
   * most of the field's life; naming it here keeps the control named without
   * an orphan label.
   */
  ariaLabel?: string
  /**
   * The catalog's names, when the caller has them. A chip whose term is not
   * among them is GRANDFATHERED: it renders and can be removed, but since
   * #1345 it cannot be typed back in. Production carries six such terms on
   * three demands. Until this they looked exactly like every other chip, so
   * removing one by accident was a one-way door with no sign on it.
   *
   * Optional because a brand-new demand (the intake form) has nothing
   * grandfathered: every chip there came out of the picker.
   */
  catalog?: string[]
  /**
   * Let a typed string that matches no catalog entry become a chip.
   *
   * **Defaults to false since #1345.** The vocabulary is controlled: the
   * catalog is curated, and free text on a demand was how six terms — five of
   * them whole sentences — reached production. The server refuses them too, so
   * turning this on would only move the refusal from the dropdown to a toast.
   *
   * Terms already stored on a demand are unaffected: they render as chips and
   * can be removed. They simply cannot be typed back in.
   */
  allowFreehand?: boolean
}

const LEGACY_NOTE = 'Not in the skills catalog. It can be removed, but not added back.'

export function SkillTypeaheadChipList({
  values,
  onSave,
  placeholder = 'Type a skill…',
  addLabel = '+ Add skill',
  disabled = false,
  allowFreehand = false,
  ariaLabel,
  catalog,
}: SkillTypeaheadChipListProps) {
  const styles = useStyles()
  const toast = useToast()
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [activeIdx, setActiveIdx] = useState(-1)
  const [showDropdown, setShowDropdown] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // The options are the whole catalog, filtered here: it is small, cached, and
  // shared with the pages that already read it. It also says which chips are
  // categories, so it is read whether or not the input is open.
  const catalogQuery = useCapabilities()
  const entries = catalogQuery.data ?? []
  // A failed request leaves the list empty, and empty is what a miss looks
  // like — so it is said apart, or a broken catalog reads as one that lacks
  // the term. A failed REFETCH keeps the last answer, which still holds.
  const loadFailed = !!catalogQuery.isError && !catalogQuery.data
  // The server's fuzzy matcher, for its "did you mean" only. Only while the
  // input is open, so a row of chips costs no request.
  const nudge = useSkillSuggestions(draft, adding).data?.nudge ?? null

  // Same identity the server's guard and drift report use: case and
  // whitespace do not make a different term.
  const catalogKeys = catalog ? new Set(catalog.map(skillKey)) : null
  const isLegacy = (v: string) => catalogKeys !== null && !catalogKeys.has(skillKey(v))
  const categoryKeys = new Set(entries.filter((c) => c.is_category).map((c) => skillKey(c.name)))
  const isCategory = (v: string) => categoryKeys.has(skillKey(v))

  // The options, searched on the draft and grouped by category, less what is
  // already a chip. A category already chosen stays as a heading over its
  // skills. `offered` is the same options in order, for the keyboard.
  const chosen = new Set(values.map(skillKey))
  const categorized = hasCategories(entries)
  const offered: Capability[] = []
  const sections: {
    key: string
    /** Names a group that has no category row of its own to do it. */
    heading: string | null
    inCategory: boolean
    first: number
    picks: Capability[]
  }[] = []
  for (const g of searchCatalog(groupCatalog(entries), draft)) {
    const category = g.category
    const pickable = category !== null && !chosen.has(skillKey(category.name))
    const picks = [
      ...(category && pickable ? [category] : []),
      ...g.skills.filter((s) => !chosen.has(skillKey(s.name))),
    ]
    if (picks.length === 0) continue
    sections.push({
      key: category ? `cat-${category.id}` : 'rest',
      heading: category ? (pickable ? null : category.name) : categorized ? 'Other' : null,
      inCategory: category !== null,
      first: offered.length,
      picks,
    })
    offered.push(...picks)
  }

  // Show freehand option if the draft is not a catalog entry — and only where
  // free text is allowed at all (#1345: not on a demand).
  const draftTrimmed = draft.trim()
  const draftEntry = draftTrimmed
    ? entries.find((c) => skillKey(c.name) === skillKey(draftTrimmed))
    : undefined
  const showFreehand = allowFreehand && draftTrimmed.length > 0 && !draftEntry

  // Total options: freehand (if shown) + the catalog's
  const freehandSlots = showFreehand ? 1 : 0
  const totalOptions = freehandSlots + offered.length

  // The nudge ("did you mean…?") is not an option — it cannot be arrowed to —
  // but it IS a reason to open the panel. #1345 made `showFreehand` false on a
  // demand, which meant a term the catalog does not have produced zero options,
  // and the panel was gated on `totalOptions > 0`. So the one input that
  // guarantees a nudge — typing something off-catalog — was the one input that
  // hid it. Open on either, and on a failed request, which has to say so.
  const showPanel = totalOptions > 0 || !!nudge || loadFailed

  function addSkill(skill: string) {
    const trimmed = skill.trim()
    if (trimmed && !values.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      onSave([...values, trimmed])
    }
    setDraft('')
    setActiveIdx(-1)
    setShowDropdown(false)
  }

  function remove(idx: number) {
    // Undo rather than a confirmation (#1245). This one needs it most: the
    // handler below fires it on a bare Backspace in an empty input, so the
    // chip vanishes from a keystroke aimed at nothing in particular.
    const before = values
    const removed = values[idx]
    onSave(before.filter((_, i) => i !== idx))
    // Not for a term the catalog no longer has: the server refuses to add it
    // back, so its Undo could only fail. Say so instead.
    if (removed !== undefined && isLegacy(removed)) {
      toast.showToast({
        title: `Removed “${removed}”`,
        body: 'It isn’t in the skills catalog, so it can’t be added back.',
      })
      return
    }
    toast.showToast({
      title: removed ? `Removed “${removed}”` : 'Removed',
      action: { label: 'Undo', onClick: () => onSave(before) },
    })
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((prev) => Math.min(prev + 1, totalOptions - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((prev) => Math.max(prev - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      // A stale index (options gone after a failed search) falls through to the
      // draft below rather than doing nothing.
      if (activeIdx >= 0 && activeIdx < totalOptions) {
        // Select the active item
        if (showFreehand && activeIdx === 0) {
          addSkill(draftTrimmed)
        } else {
          const picked = offered[activeIdx - freehandSlots]
          if (picked) addSkill(picked.name)
        }
      } else if (draftTrimmed) {
        // Already a chip. The options leave out what is already here, so this
        // fell through to "not in the skills catalog" for a term that is.
        if (values.some((v) => skillKey(v) === skillKey(draftTrimmed))) {
          toast.showToast({ title: `“${draftTrimmed}” is already added` })
          setDraft('')
          return
        }
        // Enter with nothing selected was the last freehand route in (#1345):
        // it committed the raw draft. An exact catalog hit still works — and
        // adds the CATALOG's spelling, so "DBT" becomes "dbt" rather than a
        // second casing of the same skill. Anything else says why not.
        if (draftEntry) {
          addSkill(draftEntry.name)
        } else if (allowFreehand) {
          addSkill(draftTrimmed)
        } else if (loadFailed) {
          toast.showToast({ title: 'Couldn’t load the skills catalog', intent: 'error' })
        } else {
          toast.showToast({
            title: `“${draftTrimmed}” is not in the skills catalog`,
            body: nudge?.suggestions?.length
              ? `Did you mean ${nudge.suggestions.map((s) => `“${s}”`).join(' or ')}?`
              : 'Pick one from the list. Missing one? Report it through Help and feedback.',
            intent: 'warning',
          })
        }
      }
    } else if (e.key === 'Escape') {
      setDraft('')
      setAdding(false)
      setShowDropdown(false)
    } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      // A term that left the catalog goes only by its X: removing it is
      // one-way, too much for a stray keystroke.
      const last = values[values.length - 1]
      if (last !== undefined && !isLegacy(last)) remove(values.length - 1)
    }
  }

  function renderPick(entry: Capability, optIdx: number, indented: boolean) {
    const active = activeIdx === optIdx
    const whole = !!entry.is_category
    return (
      <div
        key={entry.id}
        // Keep the keyboard's pick in sight: the list scrolls.
        ref={active ? (el) => el?.scrollIntoView?.({ block: 'nearest' }) : undefined}
        className={mergeClasses(
          styles.option,
          active && styles.optionActive,
          indented && styles.optionIndented,
        )}
        role="option"
        aria-selected={active}
        title={whole ? WHOLE_CATEGORY_NOTE : skillHoverText(entry)}
        data-category={whole ? 'true' : undefined}
        onMouseDown={(e) => {
          e.preventDefault()
          addSkill(entry.name)
        }}
      >
        {whole && <FolderRegular aria-hidden />}
        <span className={styles.optionText}>
          <span className={whole ? styles.optionCategory : undefined}>{entry.name}</span>
          {entry.description && <span className={styles.optionDesc}>{entry.description}</span>}
        </span>
        {whole && <span className={styles.optionTag}>Whole category</span>}
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.chipRow}>
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className={isLegacy(v) ? styles.chipLegacy : styles.chip}
            title={isLegacy(v) ? LEGACY_NOTE : isCategory(v) ? WHOLE_CATEGORY_NOTE : undefined}
            data-legacy={isLegacy(v) ? 'true' : undefined}
            data-category={isCategory(v) ? 'true' : undefined}
          >
            {isCategory(v) && <FolderRegular aria-hidden />}
            {v}
            {!disabled && (
              <button
                type="button"
                className={styles.chipX}
                onClick={() => remove(i)}
                aria-label={`Remove ${v}`}
              >
                <Dismiss12Regular />
              </button>
            )}
          </span>
        ))}
      </div>

      {!disabled && (
        adding ? (
          <div className={styles.inputWrapper} ref={wrapperRef}>
            <input
              ref={inputRef}
              type="text"
              autoFocus
              aria-label={ariaLabel}
              className={styles.input}
              value={draft}
              placeholder={placeholder}
              onChange={(e) => {
                setDraft(e.target.value)
                setActiveIdx(-1)
                setShowDropdown(true)
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowDropdown(true)}
              onBlur={(e) => {
                // Tab moves focus INTO the panel — to a nudge suggestion. Read
                // where focus went now, synchronously: closing the panel 200ms
                // later would unmount the button the user just tabbed onto.
                const next = e.relatedTarget as Node | null
                // Delay to allow click on dropdown option
                setTimeout(() => {
                  if (next && wrapperRef.current?.contains(next)) return
                  setShowDropdown(false)
                  // Keep the draft; do not turn it into a skill (#1247).
                  //
                  // This used to call addSkill(draftTrimmed), so typing "Mach"
                  // on the way to picking "Machine Learning" and then clicking
                  // the page background added a freehand skill called "Mach",
                  // permanently and silently.
                  //
                  // Elsewhere in this change blur COMMITS, and that is not a
                  // contradiction: there, the draft is the value. Here the value
                  // is the chip list and the draft is an unfinished selection —
                  // nobody believes they are holding a skill called "Mach". So
                  // the input keeps what was typed, ready to be finished, and
                  // only an empty draft closes it.
                  if (!draftTrimmed) setAdding(false)
                }, 200)
              }}
            />
            {/* No length gate: focusing the empty input shows the catalog.
                Browsing is the only way to learn a controlled vocabulary, and
                since #1345 this field accepts nothing else. */}
            {showDropdown && showPanel && (
              <div className={styles.dropdown}>
                {loadFailed && (
                  <div className={styles.loadError} role="alert">
                    Couldn&apos;t load the skills catalog.{' '}
                    <button
                      type="button"
                      className={styles.nudgeSuggestion}
                      // Keep focus in the input. Taking it blurs the input, and
                      // by the time the blur handler looks, the refetch has
                      // removed this button, so the panel closed on a retry.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        void catalogQuery.refetch()
                        inputRef.current?.focus()
                      }}
                    >
                      Try again
                    </button>
                  </div>
                )}
                {nudge && (
                  <div className={styles.nudge}>
                    💡 Did you mean:{' '}
                    {nudge.suggestions.map((s, i) => (
                      <span key={s}>
                        {/* A real <button>, not a styled <strong>: these are the
                            only way out for someone whose term is off-catalog,
                            and Enter/Space on a <strong onMouseDown> does
                            nothing. Same fix as EditableField's Save (#1247). */}
                        <button
                          type="button"
                          className={styles.nudgeSuggestion}
                          onClick={() => {
                            addSkill(s)
                            inputRef.current?.focus()
                          }}
                        >
                          {s}
                        </button>
                        {i < nudge.suggestions.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                    ?
                  </div>
                )}
                {/* The listbox holds options only; the alert and nudges above
                    are not options. */}
                <div role="listbox">
                  {showFreehand && (
                    <div
                      className={mergeClasses(
                        styles.freehandOption,
                        activeIdx === 0 && styles.optionActive,
                      )}
                      role="option"
                      aria-selected={activeIdx === 0}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        addSkill(draftTrimmed)
                      }}
                    >
                      Add "{draftTrimmed}" as new
                    </div>
                  )}
                  {/* Without categories the catalog is one flat group, and
                      renders as the plain list it always was. */}
                  {categorized
                    ? sections.map((sec) => (
                        <div
                          key={sec.key}
                          role="group"
                          aria-label={sec.heading ?? sec.picks[0]?.name}
                        >
                          {sec.heading && (
                            <div className={styles.groupHeading} aria-hidden="true">
                              {sec.heading}
                            </div>
                          )}
                          {sec.picks.map((entry, j) =>
                            renderPick(
                              entry,
                              freehandSlots + sec.first + j,
                              sec.inCategory && !entry.is_category,
                            ),
                          )}
                        </div>
                      ))
                    : sections.flatMap((sec) =>
                        sec.picks.map((entry, j) =>
                          renderPick(entry, freehandSlots + sec.first + j, false),
                        ),
                      )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <button type="button" className={styles.addButton} onClick={() => setAdding(true)}>
            {addLabel}
          </button>
        )
      )}
    </div>
  )
}
